import * as React from "react";
import * as _ from "underscore";
import { connect } from "react-redux";
import { Button } from "carbon-components-react";
import { Save16 } from "@carbon/icons-react";

import HurdleView from "underwriter_dashboard/shared/HurdleView";
import { loadApp } from "../actions";
import APIClient from "../../APIClient";
import { getCreditScores } from "@eave/shared/utils";

const styles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");

class CreditScorePricingHurdleEditor extends React.Component<any, any> {
  constructor(props) {
    super(props);

    this.state = {
      overallScore: props.app.decision.credit_score,
      creditScores: getCreditScores(props.app.credit_reports),
    };
  }

  findIndexOfCreditScoreWithID(creditScoreID) {
    return _.findIndex(this.state.creditScores, { id: creditScoreID });
  }

  handleCreditScoreChange(creditScoreID, fieldName) {
    const index = this.findIndexOfCreditScoreWithID(creditScoreID);
    const newState = this.state;
    const oldVal = newState.creditScores[index][fieldName];
    newState.creditScores[index][fieldName] = oldVal === null ? false : !oldVal;
    this.setState(newState, () => {
      this.setState({ overallScore: this.calcOverallScore() });
    });
  }

  static minifyBureauName(b) {
    const map = {
      Equifax: "EFX",
      Experian: "XPN",
      TransUnion: "TUC",
    };

    return map[b] || b;
  }

  // N.B. consistent with computedCreditScore() on backend
  static calcScoreForBorrower(scores) {
    if (!scores.length) return null;

    const sorted = _.sortBy(scores, (s: any) => parseInt(s.score, 10));

    if (sorted.length > 2) {
      return sorted[1].score; // second, if at least 3
    }
    return sorted[0].score; // otherwise lowest
  }

  getScoresByBorrower(filterExpr = null) {
    let scores = this.state.creditScores;
    if (filterExpr) {
      scores = _.filter(scores, filterExpr);
    }

    return _.chain(scores)
      .sortBy(s => s.score)
      .reverse() // highest to lowest
      .groupBy(s => s.borrower_entity)
      .values()
      .value();
  }

  // N.B. consistent with applicationCreditScore() on backend
  calcOverallScore() {
    const scoresByBorrower = this.getScoresByBorrower(s => s.operator_include !== false);

    const borrower1Score = CreditScorePricingHurdleEditor.calcScoreForBorrower(scoresByBorrower[0]);

    if (scoresByBorrower.length > 1) {
      return Math.min(
        borrower1Score,
        CreditScorePricingHurdleEditor.calcScoreForBorrower(scoresByBorrower[1]),
      );
    }
    return borrower1Score;
  }

  renderBorrowerScores(scores, isCoBorrower) {
    return (
      <div style={{ flex: 3 }}>
        <div style={{ padding: "2rem", border: "1px solid #959596" }}>
          <p style={{ textDecoration: "underline" }} className="text-center">
            {isCoBorrower ? `Borrower ${scores[0].borrower_entity}` : "Borrower"}
          </p>
          <table style={{ marginBottom: 0 }} className="table table-striped">
            <thead>
              <tr>
                <th className="col-md-5 text-center">Bureau</th>
                <th className="col-md-5 text-center">Score</th>
                <th className="col-md-2" />
              </tr>
            </thead>
            <tbody>
              {scores.map((s, index) => {
                const credit_scores = this.state.creditScores;

                const ignored = s.operator_include === false;
                const oneLeft =
                  _.filter(credit_scores, (s: any) => s.operator_include !== false).length === 1;

                return (
                  <tr key={index}>
                    <td className="text-center">
                      {CreditScorePricingHurdleEditor.minifyBureauName(s.bureau)}
                    </td>
                    <td className="text-center">
                      {ignored ? (
                        <span
                          style={{
                            color: "#959596",
                            textDecoration: "line-through",
                          }}
                        >
                          {s.score}
                        </span>
                      ) : (
                        <span style={{ fontWeight: "bold" }}>{s.score}</span>
                      )}
                    </td>
                    <td>
                      {ignored ? (
                        <i
                          style={{ color: "#959596", cursor: "pointer" }}
                          className="fa fa-refresh"
                          onClick={this.handleCreditScoreChange.bind(
                            this,
                            s.id,
                            "operator_include",
                          )}
                        />
                      ) : oneLeft ? (
                        <i style={{ color: "#959596" }} />
                      ) : (
                        <i
                          style={{ color: "#959596", cursor: "pointer" }}
                          className="fa fa-remove"
                          onClick={this.handleCreditScoreChange.bind(
                            this,
                            s.id,
                            "operator_include",
                          )}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  renderOverallScore() {
    return (
      <div style={{ flex: 3 }}>
        <div
          style={{
            height: "100%",
            padding: "2rem",
            border: "1px solid #959596",
          }}
        >
          <p style={{ textDecoration: "underline" }} className="text-center">
            Overall score
          </p>
          <div style={{ fontSize: 48, marginTop: 60, textAlign: "center" }}>
            {this.state.overallScore}
          </div>
        </div>
      </div>
    );
  }

  renderScores() {
    const scoresByBorrower = this.getScoresByBorrower();
    const coBorrowers = scoresByBorrower.length > 1;

    return (
      <div style={{ display: "flex" }}>
        {this.renderBorrowerScores(scoresByBorrower[0], coBorrowers)}
        {coBorrowers && (
          <div style={{ flex: 1, textAlign: "center" }}>
            <i style={{ fontSize: 36, marginTop: 96 }} className="fa fa-plus" />
          </div>
        )}
        {coBorrowers && this.renderBorrowerScores(scoresByBorrower[1], coBorrowers)}
        <div style={{ flex: 1, textAlign: "center" }}>
          <i style={{ fontSize: 36, marginTop: 96 }} className="fa fa-arrow-right" />
        </div>
        {this.renderOverallScore()}
      </div>
    );
  }

  onSave() {
    const { onHide } = this.props;
    const { creditScores } = this.state;

    APIClient.post("/credit_scores", { credit_scores: creditScores }).then(() => {
      loadApp(this.props.dispatch, this.props.app.id);
    });

    onHide();
  }

  render() {
    const { hurdle, onHide, app } = this.props;

    return (
      <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
        {this.renderScores()}
        <footer className={styles.footer}>
          <Button kind="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button kind="primary" onClick={this.onSave.bind(this)} renderIcon={Save16}>
            Save changes
          </Button>
        </footer>
      </HurdleView>
    );
  }
}

export default connect()(CreditScorePricingHurdleEditor);
